/**
 * Text sizes (overhaul plan §3.3, §3.4): exactly three tiers, snapped to whole device pixels.
 *
 * A run is rasterized at `n` device px per glyph px and drawn 1:1, never scaled. With
 * `x = s·f` (`s` = the tier's scale, `f` = device px per stage px): n = round(x), minus one when
 * that overshoots x by more than 20 %. So text is at most 20 % bigger than nominal and never
 * drops a step early. `LOGO` is the one non-tier size (owner Q5): the title-card wordmark, with its
 * two portrait sizes `LOGO6` (×6) and `LOGO5` (×5, under 360 stage px wide) (portrait plan §Y8).
 */
import type { FontName } from './font.ts';

export type Tier = 'L' | 'M' | 'S';
/** The tiers plus the single logo exception. */
export type TextSize = Tier | 'LOGO' | 'LOGO6' | 'LOGO5';

export interface TierSpec {
  readonly font: FontName;
  /** nominal glyph-pixel scale in stage px */
  readonly scale: number;
}

export const TIERS: Readonly<Record<TextSize, TierSpec>> = Object.freeze({
  L: { font: '5x7', scale: 3 },
  M: { font: '5x7', scale: 2 },
  S: { font: '3x5x', scale: 2 },
  LOGO: { font: '5x7', scale: 8 },
  LOGO6: { font: '5x7', scale: 6 },
  LOGO5: { font: '5x7', scale: 5 },
});

/** Device px per glyph px for a nominal glyph size of `x = s·f` device px. */
export function snap(x: number): number {
  let n = Math.max(1, Math.round(x));
  if (n > 1 && n > 1.2 * x + 1e-9) n -= 1;
  return n;
}

export interface TierRun {
  font: FontName;
  /** nominal scale in stage px (1 for the 5x7 ×1 substitute of S) */
  scale: number;
  /** device px per glyph px */
  n: number;
}

/** Font and raster size of a tier at UI scale `f`. S at n = 1 uses 5x7 (no glyph below 7 device px). */
export function tierRun(tier: TextSize, f: number): TierRun {
  const t = TIERS[tier];
  const n = snap(t.scale * f);
  if (tier === 'S' && n === 1) return { font: '5x7', scale: 1, n };
  return { font: t.font, scale: t.scale, n };
}

const FONT_H: Readonly<Record<FontName, number>> = { '5x7': 7, '3x5': 5, '3x5x': 5 };

/** Glyph height of a tier at `f` in device px (`font height · n`): 21/14/10 at 1080p. */
export function glyphHeight(tier: TextSize, f: number): number {
  const r = tierRun(tier, f);
  return FONT_H[r.font] * r.n;
}
